import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../contexts/ConfigContext';

// Components
import Meta from '../shared/Meta';
import Triple from './content/Triple';
import LogoList from './content/LogoList';
import ResponsiveImg from '../shared/ResponsiveImg';
import VendorFaq from './faqs/VendorFaq';
import AnyEvent from './content/AnyEvent';

import styles from '../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from './content/Triple.module.postcss';
import darkSectionStyles from './content/AnyEvent.module.postcss';

const getVendorEvents = (vehicleType) => [
  {
    title: 'Custom Business Profile',
    text: `Showcase your ${vehicleType} with a dedicated page featuring your menu, photos, and contact info to attract more customers.`,
    icon: '/static/images/admin/catering/employee.svg',
    width: 'sm'
  },
  {
    title: 'Catering Leads Sent to You',
    text: 'Receive exclusive event and catering requests directly from businesses, event planners, and hungry customers.',
    icon: 'https://static.seattlefoodtruck.com/badges/truck.webp',
    width: 'lg'
  },
  {
    title: 'Get Featured in Front of Thousands',
    text: `With tens of thousands of daily visitors, more eyes on your ${vehicleType} means more bookings and more sales!`,
    icon: 'https://static.seattlefoodtruck.com/badges/stage.svg'
  },
  {
    title: 'Calendar Syncing with Google & More',
    text: 'Seamlessly sync your schedule with Gmail, Outlook, or other calendars so you never miss a booking.',
    icon: 'https://static.seattlefoodtruck.com/badges/most-booked.webp',
    width: 'lg'
  },
  {
    title: 'Online Ordering for Customers',
    text: 'Enable pre-orders and mobile ordering to boost sales and keep lines moving fast.',
    icon: 'https://static.seattlefoodtruck.com/badges/cards.webp',
    width: 'lg'
  },
  {
    title: 'Social Media Integration & Auto-Posting',
    text: `Connect your Instagram, Facebook, and Twitter for automated promotions and live updates from your ${vehicleType} page.`,
    icon: 'https://static.seattlefoodtruck.com/badges/party.svg'
  },
  {
    title: 'Customer Reviews & Ratings',
    text: 'Build credibility with customer testimonials that help you stand out and attract more business.',
    icon: 'https://static.seattlefoodtruck.com/badges/most-liked.webp',
    width: 'lg'
  },
  {
    title: 'Direct Customer Bookings',
    text: 'Let customers contact you instantly for event inquiries, catering jobs, and private bookings.',
    icon: 'https://static.seattlefoodtruck.com/badges/contactless.webp',
    width: 'lg'
  },
  {
    title: 'No Commission Fees on Caterings',
    text: "Keep 100% of what you earn—we don't take a cut from your catering jobs.",
    icon: 'https://static.seattlefoodtruck.com/badges/bubbles.svg'
  },
  {
    title: 'Free to Join, No Risk!',
    text: "Signing up is completely free—there's no catch, no hidden fees, and no risk to you!",
    icon: 'https://static.seattlefoodtruck.com/badges/sft-favs.webp',
    width: 'lg'
  }
];

function Vendors() {
  const { site_title, vehicle_type, city_name, site_title_spaces } = useContext(ConfigContext);
  const siteTitle = site_title_spaces.slice(0, -4);
  const vehicleType = vehicle_type.toLowerCase();

  const vendorEvents = getVendorEvents(vehicleType);

  return (
    <div>
      <Meta
        title='Vendors'
        breadcrumbName='Vendors'
        description={`Grow your food ${vehicleType} business in ${city_name}. Get catering leads, vending opportunities, and access to corporate campuses, breweries, and events through ${site_title}.`}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <div className={styles.Cta_image}>
            <ResponsiveImg
              src='https://static.seattlefoodtruck.com/photos/vendors-header.webp'
              widths={[400, 600, 800, 1000]}
              sizes='(max-width: 900px) 100vw, 50vw'
              alt='Vendors'
              loading='eager'
              fetchpriority='high'
            />
          </div>
          <div className={styles.Cta_copy}>
            <h1>Get More Customers & Book More Events with {siteTitle}!</h1>
            <p>{site_title} connects you with high-traffic spots, top breweries, corporate campuses, and thriving communities. Get access to exclusive vending locations, major catering contracts, and thousands of local customers — all in one place.</p>
            <div>
              <Link to='/vendor/register' className='Button Button--mobile-w100'>Join Now — It&#39;s Free!</Link>
            </div>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Partner with companies like' className='u-mt1' />
      </section>

      <section className='Section'>
        <div className={tripleStyles.Triple_wrapper + ' u-mb1'}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/vendors1.webp'
            isResponsive
            title='💰 Steady & Predictable Sales'
            text="Join markets with consistent foot traffic and repeat customers, giving you a reliable revenue stream without the guesswork."
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/vendors2.webp'
            isResponsive
            title='📍 Reach More Customers'
            text={`${city_name}'s busiest markets attract locals and tourists looking for great food. Set up your ${vehicle_type.toLowerCase()} in high-traffic areas and get in front of thousands of potential new customers.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/vendors3.webp'
            isResponsive
            title='🚀 Boost Your Brand Visibility'
            text={`Get featured on ${site_title} and our marketing channels to expand your reach, grow your following, and build a loyal customer base.`}
          />
        </div>
        <div className='u-textCenter u-mb2'>
          <Link to='/vendor/register' className='Button'>Sign Up Now</Link>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <VendorFaq />
      </section>

      <AnyEvent title='Your Free Business Page Includes:' events={vendorEvents} />

      <section className={cn('Section', darkSectionStyles.DarkSection)}>
        <div className={darkSectionStyles.DarkSection__centered}>
          <h2 className='u-mb1'>🔥 Fueling {city_name}&#39;s Food {vehicle_type} Scene</h2>
          <p>Get your food {vehicle_type.toLowerCase()} in front of thousands of hungry customers every day! List your business on {site_title} to book more catering gigs, secure top vending spots, and grow your brand — hassle-free.</p>
          <p className='u-mt2'>
            <Link to='/vendor/register' className='Button'>Get Started Today</Link>
          </p>
        </div>
      </section>

    </div>
  );
}

export default Vendors;
